import React, {useState, useRef} from 'react';
import PropTypes from 'prop-types';
import {Link as RouterLink} from 'react-router-dom';
import Link from '@material-ui/core/Link';
import {fade, makeStyles} from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import InputBase from '@material-ui/core/InputBase';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import AccountCircle from '@material-ui/icons/AccountCircle';
import MoreIcon from '@material-ui/icons/MoreVert';
import Avatar from '@material-ui/core/Avatar';
import classNames from 'classnames';
import {useLocation} from 'react-router';
import PaletteIcon from '@material-ui/icons/Palette';

import {SearchFocus} from '&/utils/hotKeys';
import {userLink, userAvatar} from '&/utils/users';

const useStyles = makeStyles(theme => ({
	root: {
		zIndex: theme.zIndex.drawer + 1,
	},
	grow: {
		flexGrow: 1,
	},
	avatar: {
		marginLeft: 10,
		cursor: 'pointer',
	},
	menuButton: {
		marginRight: theme.spacing(2),
	},
	title: {
		display: 'block',
		[theme.breakpoints.up('sm')]: {
			display: 'block',
		},
	},
	search: {
		position: 'relative',
		borderRadius: theme.shape.borderRadius,
		backgroundColor: fade(theme.palette.common.white, 0.15),
		'&:hover': {
			backgroundColor: fade(theme.palette.common.white, 0.25),
		},
		marginRight: theme.spacing(2),
		marginLeft: 0,
		width: '100%',
		[theme.breakpoints.up('sm')]: {
			marginLeft: theme.spacing(3),
			width: 'auto',
		},
	},
	searchIcon: {
		width: theme.spacing(7),
		height: '100%',
		position: 'absolute',
		pointerEvents: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	inputRoot: {
		color: 'inherit',
	},
	inputInput: {
		padding: theme.spacing(1, 1, 1, 7),
		transition: theme.transitions.create('width'),
		width: '100%',
		[theme.breakpoints.up('sm')]: {
			width: 140,
			'&:focus': {
				width: 200,
			},
		},
	},
	rightSide: {
		display: 'flex',
		alignItems: 'center',
	},
	sectionDesktop: {
		display: 'none',
		[theme.breakpoints.up('md')]: {
			display: 'flex',
		},
	},
	sectionMobile: {
		display: 'flex',
		[theme.breakpoints.up('md')]: {
			display: 'none',
		},
	},
}));

const Header = ({auth, openDrawer, openThemePicker, handleSearchChange, logout}) => {
	const title = process.env.REACT_APP_TITLE;

	const location = useLocation();

	const classes = useStyles();

	const searchRef = useRef(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);

	const isMenuOpen = Boolean(anchorEl);
	const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

	const handleAppearance = () => {
		handleMenuClose();
		handleMobileMenuClose();

		openThemePicker();
	};

	const handleProfileMenuOpen = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleMobileMenuClose = () => {
		setMobileMoreAnchorEl(null);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		handleMobileMenuClose();
	};

	const handleLogout = () => {
		handleMenuClose();
		logout();
	};

	const handleMobileMenuOpen = e => {
		setMobileMoreAnchorEl(e.currentTarget);
	};

	const menuId = 'primary-search-account-menu';
	const renderMenu = auth.isAuth && (
		<Menu
			anchorEl={anchorEl}
			anchorOrigin={{vertical: 'top', horizontal: 'right'}}
			id={menuId}
			keepMounted
			transformOrigin={{vertical: 'top', horizontal: 'right'}}
			open={isMenuOpen}
			onClose={handleMenuClose}
		>
			<Link underline='none' component={RouterLink} to={userLink(auth.user)} color={'inherit'}>
				<MenuItem onClick={handleMenuClose}>My profile</MenuItem>
			</Link>
			<MenuItem onClick={handleAppearance}>Appearance</MenuItem>
			<MenuItem onClick={handleLogout}>Logout</MenuItem>
		</Menu>
	);

	const mobileMenuId = 'primary-search-account-menu-mobile';
	const renderMobileMenu = (
		<Menu
			anchorEl={mobileMoreAnchorEl}
			anchorOrigin={{vertical: 'top', horizontal: 'right'}}
			id={mobileMenuId}
			keepMounted
			transformOrigin={{vertical: 'top', horizontal: 'right'}}
			open={isMobileMenuOpen}
			onClose={handleMobileMenuClose}
		>
			<MenuItem onClick={handleAppearance}>
				<IconButton>
					<PaletteIcon />
				</IconButton>
				<p>Appearance</p>
			</MenuItem>

			{auth.isAuth ? (
				<div>
					<MenuItem onClick={handleProfileMenuOpen}>
						<IconButton
							aria-label='Account of current user'
							aria-controls='primary-search-account-menu'
							aria-haspopup='true'
						>
							<AccountCircle />
						</IconButton>
						<p>{auth.user.firstName}</p>
					</MenuItem>
				</div>
			) : (
				<Link
					underline='none'
					component={RouterLink}
					to={{pathname: '/auth', state: {from: location}}}
					color='inherit'
				>
					<MenuItem onClick={handleMenuClose}>
						<IconButton>
							<AccountCircle />
						</IconButton>
						<p>Auth</p>
					</MenuItem>
				</Link>
			)}
		</Menu>
	);

	return (
		<header className={classes.root}>
			<SearchFocus action={() => searchRef.current.focus()} />

			<div className={classes.grow}>
				<AppBar position='fixed' color={'primary'}>
					<Toolbar>
						{auth.isAuth && (
							<IconButton
								edge='start'
								className={classes.menuButton}
								aria-label='Open drawer'
								onClick={openDrawer}
								color='inherit'
							>
								<MenuIcon />
							</IconButton>
						)}
						<Typography className={classes.title} variant='h5' noWrap>
							<Link underline='none' component={RouterLink} to='/' color={'inherit'}>
								{title}
							</Link>
						</Typography>

						<div className={classes.grow} />
						<div className={classes.sectionDesktop}>
							{auth.isAuth ? (
								<div className={classes.rightSide}>
									<Avatar
										className={classNames(classes.avatar, 'avatar')}
										style={{backgroundColor: auth.user.avatar}}
										onClick={handleProfileMenuOpen}
									>
										{userAvatar(auth.user)}
									</Avatar>
								</div>
							) : (
								<Link
									underline='none'
									component={RouterLink}
									to={{pathname: '/auth', state: {from: location}}}
									color={'inherit'}
								>
									<IconButton color='inherit'>
										<AccountCircle />
									</IconButton>
								</Link>
							)}
						</div>
						<div className={classes.sectionMobile}>
							<IconButton
								aria-label='Show more'
								aria-controls={mobileMenuId}
								aria-haspopup='true'
								onClick={handleMobileMenuOpen}
								color='inherit'
							>
								<MoreIcon />
							</IconButton>
						</div>
					</Toolbar>
				</AppBar>
				{renderMobileMenu}
				{renderMenu}
			</div>
		</header>
	);
};

Header.propTypes = {
	auth: PropTypes.shape({
		isAuth: PropTypes.bool,
		isAdmin: PropTypes.bool,
		user: PropTypes.shape({
			_id: PropTypes.string,
			firstName: PropTypes.string,
		}),
	}),
	openDrawer: PropTypes.func,
	openThemePicker: PropTypes.func,
	handleSearchChange: PropTypes.func,
	logout: PropTypes.func,
};

export default Header;
